import { startTransition, useState } from 'react';

import useClientState from '@/state/useClientState';

import { SearchAutocomplete, SearchModality, SearchParams, SearchType } from '../../types';
import { SEARCH_ADD_TOGGLE_STORAGE_KEY } from '../utils/helpers';
import { mapChipToSearchType } from '../utils/searchAutocomplete';
import { formatQuery, getSearchValue } from '../utils/searchInput';
import { validateSearchQuery } from '../utils/searchValidations';

import useSearchAutocomplete from './useSearchAutocomplete';

type SearchPanelType = false | 'searchPanel' | 'searchDates' | 'materieeltypen' | 'searchAutocomplete';
export type SearchInputState = {
  /**
   * Afgeleide van de query om autosuggestions te kunnen laten zien.
   */
  begintMet: string;
  query: string;
  /** @alias beginMet */
  rawQuery: string;
  searchType: SearchType;

  previewSearchType: SearchType;

  searchDate: string | undefined | null;
  showPanel: SearchPanelType;

  selectedIndex: number;

  isFocused: boolean;
  isInputChipActive: boolean;

  createSearchParams?: (searchDate: { searchDate?: string | null }) => SearchParams;
  hasError: boolean;
  isValid: boolean;
  isValidMaterieelTypeValue: boolean;
  isValidOmloopValue: boolean;
  isValidSingleMultipleOrRangeValue: boolean;

  /** To add or overwrite the searchResults */
  addSearchResults: boolean;

  searchModality: SearchModality;
};
function defaultSearchState(): SearchInputState {
  return {
    query: '',
    begintMet: '',
    rawQuery: '',
    searchType: 'empty',
    previewSearchType: 'empty',
    searchDate: undefined,
    showPanel: false,
    selectedIndex: 0,
    isFocused: false,
    isInputChipActive: false,
    isValid: false,
    hasError: false,
    isValidMaterieelTypeValue: false,
    isValidOmloopValue: false,
    isValidSingleMultipleOrRangeValue: false,
    addSearchResults: false,
    searchModality: 'single',
  };
}
const useSearchState = (isWithinAdvancedSearch: boolean) => {
  const showAdvancedSearchDialog = useClientState((state) => state.showAdvancedSearchDialog);

  const { suggestions, requestSuggestions, clearSuggestions } = useSearchAutocomplete();
  const [searchState, setSearchState] = useState<SearchInputState>(defaultSearchState());

  /**
   * Determine which searchPanel to show.
   *
   * - When a user focusses on the search input field, it should show 'searchPanel'.
   * - When a user starts typeing, it should show the searchAutocomplete field.
   * - When a user has selected a autosuggestion value it should still be able to continue searching with other values.
   * - When a user has selected another searchAutocomplete value, should they always be of the samen type.
   *
   * - It should return false if the user does not have focussed the searchInput field.
   * @todo - It should close when opening the showAdvancedSearchDialog. Maybe make showSearchPanel a state item.
   *
   */
  const getPanel = (state: SearchInputState): SearchPanelType => {
    const { query, searchType, isFocused, searchModality } = state;
    if (!isFocused || (!isWithinAdvancedSearch && showAdvancedSearchDialog)) return false;
    if (searchType === 'empty' && query.length === 0) return 'searchPanel';
    if (isWithinAdvancedSearch) return false;

    if (query.length > 0 && !['json'].includes(searchModality) && searchType !== 'json') return 'searchAutocomplete';

    return false;
  };

  const [addSearchResults, setAddSearchResults] = useState<boolean>(
    sessionStorage?.getItem(SEARCH_ADD_TOGGLE_STORAGE_KEY) === 'true',
  );

  function resetState() {
    setSearchState(defaultSearchState());
  }
  function closeExtendedSearch() {
    setSearchState((prev) => ({ ...prev, showPanel: false, isFocused: false, selectedIndex: 0 }));
  }
  function handleOnFocus() {
    if (!isWithinAdvancedSearch) {
      setSearchState((prev) => {
        const isFocused = true;
        return {
          ...prev,
          isFocused,
          rawQuery: prev.query !== '' ? prev.query : '',
          previewSearchType: prev.previewSearchType !== prev.searchType ? prev.searchType : prev.previewSearchType,
          showPanel: getPanel({ ...prev, isFocused }),
        };
      });
    }
  }
  function handleOnClick() {
    if (isWithinAdvancedSearch) {
      setSearchState((prev) => {
        const isFocused = true;
        return { ...prev, isFocused, showPanel: getPanel({ ...prev, isFocused }) };
      });
    }
  }

  // infinite loop
  function handleOnChange(value: string) {
    setSearchState((prev) => {
      const validation = validateSearchQuery({
        query: value,
        searchType: prev.searchType,
        isWithinAdvancedSearch,
      });

      // Remove the suggestions if it should re-evaluate.
      if (validation.shouldValidate) clearSuggestions();

      // validation.formattedInput should only be used upon searching
      // Its safe to leave it with the query itself, but testcase fix implications will be big.
      const newState: SearchInputState = {
        ...prev,
        query: value,
        rawQuery: value,
        ...(validation as unknown as Partial<SearchInputState>),
      };
      if (!validation.shouldValidate) {
        newState.previewSearchType = prev.searchType;
      }
      newState.showPanel = getPanel(newState);

      if (newState.showPanel === 'searchAutocomplete' && validation.begintMet) {
        startTransition(() => {
          requestSuggestions({
            begintMet: String(validation.begintMet),
            filterBySearchType: prev.searchType === 'empty' ? [] : [prev.searchType],
            treindatum: prev.searchDate,
          });
        });
      }
      newState.selectedIndex = prev.showPanel === 'searchAutocomplete' ? 0 : prev.selectedIndex;
      return { ...newState };
    });
  }
  function handleSelectSearchType(searchType: SearchType, showInsideInput: boolean = false) {
    setSearchState((prev) => ({
      ...prev,
      searchType,
      previewSearchType: searchType,
      selectedIndex: 0,
      isInputChipActive: showInsideInput,
      // searchDate: undefined,
    }));
  }
  function clearSearchType() {
    setSearchState((prev) => ({
      ...prev,
      searchType: 'empty',
      previewSearchType: 'empty',
      isInputChipActive: false,
    }));
  }
  function setSelectedIndex(index: number) {
    setSearchState((prev) => ({ ...prev, selectedIndex: index }));
  }

  /**
   * @alias setDefaultValues
   * @param historyItem
   * @returns
   */
  function setHistoryItem(historyItem: SearchParams) {
    if (!historyItem || (isWithinAdvancedSearch && historyItem.searchType === 'rollingStockType')) return;
    setSearchState((prev) => ({
      ...prev,
      query: String(getSearchValue(historyItem)),
      searchType: historyItem.searchType,
      selectedIndex: 0,
      isInputChipActive: true,
      ...(validateSearchQuery({
        query: String(getSearchValue(historyItem)),
        searchType: historyItem.searchType,
        isWithinAdvancedSearch,
      }) as unknown as Partial<SearchInputState>),
      showPanel: false, // Close the panel and search
    }));
  }
  function setSearchDateV2(date: string | null) {
    setSearchState((prev) => {
      const newState = { ...prev, searchDate: date };
      if (newState.showPanel === 'searchAutocomplete' && newState.begintMet) {
        startTransition(() => {
          requestSuggestions({
            begintMet: String(newState.begintMet),
            filterBySearchType: prev.searchType === 'empty' ? [] : [prev.searchType],
            treindatum: date,
          });
        });
      }
      return newState;
    });
  }
  function setIsFocused(value: boolean) {
    setSearchState((state) => {
      return { ...state, isFocused: value };
    });
  }
  const [showJSONChip, setShowJSONChip] = useState<boolean>(false);
  const handleShowJSONButtonClicks = () => {
    let clickCount = 0;
    let lastClickTime = 0;
    return (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const currentTime = Date.now();
      if (currentTime - lastClickTime > 1000) clickCount = 0; // Reset after a 1 second.
      lastClickTime = currentTime;
      clickCount++;
      if (clickCount >= 20) setShowJSONChip(true);
    };
  };

  /**
   * Preview an autocomplete suggestion.
   * @param suggestion
   * @param index
   */
  function returnAutocompleteSuggestion(suggestion: SearchAutocomplete, index: number): SearchParams | undefined {
    const query = suggestion?.waarde ? `${suggestion.waarde}` : `${suggestion?.van} - ${suggestion?.tot}`;
    const searchType = mapChipToSearchType(suggestion.chip);
    if (query) {
      let newState;
      setSearchState((prev) => {
        const newQuery = formatQuery(prev.query, query, searchType);
        newState = {
          ...prev,
          searchType,
          query: newQuery,
          selectedIndex: index,
          isInputChipActive: true,
          ...(validateSearchQuery({
            searchType,
            query: newQuery,
            isWithinAdvancedSearch,
          }) as unknown as Partial<SearchInputState>),
        };
        return { ...newState };
      });
      if (newState) {
        const { createSearchParams, searchDate } = newState;
        return createSearchParams({ searchDate });
      }
    }
  }

  return {
    ...searchState,

    searchState,
    suggestions,
    focused: searchState.isFocused,
    setSearchState,
    addSearchResults,
    setAddSearchResults,
    showJSONChip,
    handleShowJSONButtonClicks,
    resetState,
    closeExtendedSearch,
    handleOnFocus,
    handleOnClick,
    handleOnChange,
    handleSelectSearchType,
    clearSearchType,
    setSelectedIndex,
    setHistoryItem,
    setIsFocused,
    setSearchDateV2,
    returnAutocompleteSuggestion,
  };
};
export default useSearchState;
